import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';

const DocumentTypeSelectorModal = ({ 
  show, 
  onClose, 
  project, 
  onSelect,
  action = 'revise' // 'revise', 'approve', or 'upload'
}) => {
  const { documentTypes, getDocumentRevisionFee } = useAppContext();
  const [selectedType, setSelectedType] = useState('');
  
  if (!show || !project) return null;
  
  // Get all document types that have been uploaded by planAIdesign (not user uploads)
  const uploadedDocTypes = [];
  project.deliverables?.forEach(d => {
    if (d.documentType && d.type !== 'quote-document' && d.type !== 'user-upload') {
      // Check if this document type is already in the list
      if (!uploadedDocTypes.find(item => item.value === d.documentType)) {
        const tracking = project.documentRevisionTracking?.[d.documentType];
        const nextRevision = tracking ? tracking.revisionsUsed + 1 : 1;
        const isAdditional = tracking && nextRevision > tracking.maxIncluded;
        const fee = isAdditional ? getDocumentRevisionFee(project.stage, d.documentType, nextRevision) : 0;
        
        // Check if already approved
        const isApproved = project.approvedDocuments?.[d.documentType];
        
        uploadedDocTypes.push({
          value: d.documentType,
          label: documentTypes[`stage${project.stage}`][d.documentType],
          tracking: tracking,
          nextRevision: nextRevision,
          isAdditional: isAdditional,
          fee: fee,
          uploadDate: d.uploadDate,
          currentVersion: d.version || 0,
          isApproved: isApproved,
          approvedDate: isApproved?.approvedDate
        });
      }
    }
  });
  
  // For upload action, show all available document types for the stage
  let availableDocTypes = uploadedDocTypes;
  
  if (action === 'upload') {
    const stageDocTypes = documentTypes[`stage${project.stage}`] || {};
    availableDocTypes = Object.entries(stageDocTypes).map(([key, label]) => ({
      value: key,
      label: label,
      isNewUpload: true,
      currentVersion: 0,
      uploadDate: 'Not uploaded yet'
    }));
  }
  
  const handleSelect = () => {
    if (!selectedType) {
      return;
    }
    
    const selected = availableDocTypes.find(d => d.value === selectedType);
    if (selected) {
      onSelect(selected);
    }
    onClose();
  };
  
  const isApproveAction = action === 'approve';
  const isUploadAction = action === 'upload';
  
  // Get the correct title based on action
  const getTitle = () => {
    if (isUploadAction) return 'Select Document Type to Upload';
    if (isApproveAction) return 'Select Document Type to Approve';
    return 'Select Document Type for Revision';
  };
  
  // Get the correct description based on action
  const getDescription = () => {
    if (isUploadAction) return 'Please select which document type you would like to upload:';
    if (isApproveAction) return 'Please select which document type you would like to approve:';
    return 'Please select which document type you would like to request a revision for:';
  };
  
  // Get the correct button text based on action
  const getButtonText = () => {
    if (isUploadAction) return 'Continue with Upload';
    if (isApproveAction) return 'Approve Document';
    return 'Continue with Revision';
  };
  
  // Get the correct button color based on action
  const getButtonColor = () => {
    if (isUploadAction) return 'bg-blue-600 hover:bg-blue-700';
    if (isApproveAction) return 'bg-blue-600 hover:bg-blue-700';
    return 'bg-orange-600 hover:bg-orange-700';
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            {getTitle()}
          </h3>
          <button 
            onClick={onClose}
            className="text-2xl text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            {getDescription()}
          </p>
          
          <div className="space-y-3">
            {availableDocTypes.map((docType) => (
              <div
                key={docType.value}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedType === docType.value 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedType(docType.value)}
              >
                <div className="flex items-start">
                  <input
                    type="radio"
                    name="documentType"
                    value={docType.value}
                    checked={selectedType === docType.value}
                    onChange={() => setSelectedType(docType.value)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg">
                      {docType.label}
                      {docType.isApproved && (
                        <span className="ml-2 text-sm text-green-600">✅ Previously Approved</span>
                      )}
                      {docType.isNewUpload && (
                        <span className="ml-2 text-sm text-blue-600">📝 New Upload</span>
                      )}
                    </h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      {!docType.isNewUpload && (
                        <>
                          <p>📄 Current Version: {docType.currentVersion}</p>
                          <p>📅 Last Upload: {docType.uploadDate}</p>
                        </>
                      )}
                      {docType.isNewUpload && (
                        <p>📝 This document type has not been uploaded yet</p>
                      )}
                      {docType.isApproved && docType.approvedDate && (
                        <p className="text-green-600">
                          ✅ Last Approved: {new Date(docType.approvedDate).toLocaleDateString()}
                        </p>
                      )}
                      {!isApproveAction && !isUploadAction && docType.tracking && (
                        <>
                          <p>🔄 Revisions Used: {docType.tracking.revisionsUsed} of {docType.tracking.maxIncluded} included</p>
                          {docType.isAdditional && (
                            <p className="text-orange-600 font-semibold">
                              💰 Next revision will cost: ${docType.fee}
                            </p>
                          )}
                          {!docType.isAdditional && (
                            <p className="text-green-600 font-semibold">
                              ✅ Next revision is included (free)
                            </p>
                          )}
                        </>
                      )}
                      {!isApproveAction && !isUploadAction && !docType.tracking && (
                        <p className="text-green-600 font-semibold">
                          ✅ First revision is included (free)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {availableDocTypes.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No documents available for {isUploadAction ? 'upload' : isApproveAction ? 'approval' : 'revision'}
            </div>
          )}
        </div>
        
        <div className="flex gap-4">
          <button 
            onClick={onClose}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
          >
            Cancel
          </button>
          <button 
            onClick={handleSelect}
            disabled={!selectedType}
            className={`flex-1 text-white py-3 rounded-lg font-semibold disabled:bg-gray-400 ${getButtonColor()}`}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentTypeSelectorModal;